import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, CalendarDays, ClipboardList, Wallet, MoreHorizontal, Users, Ship, FileText, LayoutTemplate, Settings, LogOut, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const MAIN = [
  { to: "/admin", label: "Home", icon: Home, end: true },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/admin/bookings", label: "Bookings", icon: ClipboardList },
  { to: "/admin/finance", label: "Keuangan", icon: Wallet },
];
export const EXTRA = [
  { to: "/admin/manifest", label: "Guest Manifest", icon: Users },
  { to: "/admin/boats", label: "Boats", icon: Ship },
  { to: "/admin/invoices", label: "Invoices", icon: FileText },
  { to: "/admin/content", label: "Website Content", icon: LayoutTemplate },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export const logout = async (navigate) => {
  await api.post("/auth/logout").catch(() => {});
  navigate("/admin/login", { replace: true });
};

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 h-12 rounded-xl text-[15px] font-semibold transition-colors duration-150 ${isActive ? "bg-soft text-oceandeep" : "text-slate-600 hover:bg-sand"}`;

const Sidebar = ({ user }) => {
  const navigate = useNavigate();
  return (
    <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-white h-screen sticky top-0 p-4" data-testid="admin-sidebar">
      <div className="flex items-center gap-3 px-2 pb-6">
        <img src="/logo.png" alt="Indo Aura Dive" className="w-12 h-12 object-contain" />
        <div><p className="font-extrabold leading-tight">Indo Aura</p><p className="text-xs text-slate-500">Admin</p></div>
      </div>
      <nav className="flex flex-col gap-1 flex-1 overflow-y-auto">
        {[...MAIN, ...EXTRA].map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} className={linkCls} data-testid={`sidebar-${l.label.toLowerCase().replace(/ /g, "-")}`}>
            <l.icon className="w-5 h-5" />{l.label}
          </NavLink>
        ))}
      </nav>
      <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 h-11 text-sm text-slate-600 hover:text-ink" data-testid="sidebar-view-site"><ExternalLink className="w-4 h-4" />View website</a>
      <div className="border-t border-border pt-3 mt-2 flex items-center gap-3 px-2">
        {user.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 rounded-full bg-soft" />}
        <p className="text-xs text-slate-600 truncate flex-1" data-testid="admin-user-email">{user.email}</p>
        <button onClick={() => logout(navigate)} className="p-2 rounded-lg hover:bg-sand" data-testid="logout-btn" aria-label="Log out"><LogOut className="w-4 h-4" /></button>
      </div>
    </aside>
  );
};

const BottomBar = () => (
  <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-border grid grid-cols-5 pb-safe" data-testid="admin-bottom-bar">
    {[...MAIN, { to: "/admin/more", label: "More", icon: MoreHorizontal }].map((l) => (
      <NavLink key={l.to} to={l.to} end={l.end} data-testid={`tab-${l.label.toLowerCase()}`}
        className={({ isActive }) => `flex flex-col items-center justify-center gap-1 h-16 text-[11px] font-semibold ${isActive ? "text-oceandeep" : "text-slate-500"}`}>
        <l.icon className="w-6 h-6" />{l.label}
      </NavLink>
    ))}
  </nav>
);

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (user) return;
    api.get("/auth/me").then((r) => setUser(r.data))
      .catch((e) => navigate("/admin/login", { replace: true, state: e?.response?.status === 403 ? { error: errMsg(e) } : undefined }));
  }, [user, navigate]);

  if (!user) return <div className="min-h-screen grid place-items-center text-slate-500 font-admin" data-testid="admin-loading">Checking access…</div>;
  return (
    <div className="min-h-screen flex bg-[#FAF9F7] font-admin text-ink notranslate" translate="no">
      <Sidebar user={user} />
      <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 pt-5 lg:pt-8 pb-28 lg:pb-12">
        <Outlet context={{ user }} />
      </main>
      <BottomBar />
    </div>
  );
}
